import React from 'react'
import { graphql, useLazyLoadQuery } from 'react-relay'
import { useParams } from 'react-router-dom'

import { useScreenMessages } from '@data/i18n/useScreenMessages'

import { EventMetadata } from '@newComponents/Event/EventMetadata'
import { NotFoundScreen } from '@newComponents/NotFoundScreen'
import { Screen, PaletteProvider } from '@newComponents'

import { getSlugFromPrettyUrl } from '@data/util/urlHelpers'

import { EventScreenNewDesktop } from './EventScreenNewDesktop'
import { EventScreenNewMobile } from './EventScreenNewMobile'

import { EventScreenNewQuery } from './__generated__/EventScreenNewQuery.graphql'

const resolvePalette = (event: EventScreenNewQuery['response']['event']) => {
  if (!event) return null
  if (event.coverPhoto?.colorPalette) return event.coverPhoto.colorPalette
  const owner = event.owner
  if (owner?.__typename === 'Guild' && owner.primaryPhoto?.colorPalette) {
    return owner.primaryPhoto.colorPalette
  }
  if (owner?.__typename === 'User' && owner.primaryPhoto?.colorPalette) {
    return owner.primaryPhoto.colorPalette
  }
  return null
}

export const EventScreen = () => {
  useScreenMessages((locale) => import(/* webpackChunkName: "locale-event-screen-[request]" */ `./locales/${locale}.po`))

  const params = useParams<'eventId'>()

  const data = useLazyLoadQuery<EventScreenNewQuery>(
    graphql`
      query EventScreenNewQuery($slug: String!) {
        event: eventBySlug(slug: $slug) {
          # Palette data
          coverPhoto {
            colorPalette
          }
          owner {
            __typename
            ... on Guild {
              primaryPhoto {
                colorPalette
              }
            }
            ... on User {
              primaryPhoto {
                colorPalette
              }
            }
          }

          ...EventMetadata_event
          ...EventScreenNewDesktop_event
          ...EventScreenNewMobile_event
        }

        ...EventScreenNewDesktop_query
        ...EventScreenNewMobile_query
      }
    `,
    { slug: getSlugFromPrettyUrl(params.eventId) }
  )

  if (!data.event) {
    return <NotFoundScreen />
  }

  return (
    <PaletteProvider palette={resolvePalette(data.event)}>
      <EventMetadata event={data.event} />

      <Screen>
        <EventScreenNewMobile event={data.event} query={data} />
        <EventScreenNewDesktop event={data.event} query={data} />
      </Screen>
    </PaletteProvider>
  )
}

export default EventScreen
